import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import { ExpandMore, ExpandLess, Close } from "@mui/icons-material";
import axios from "axios";
import colors from "../constants/colors";
import { URL } from "../constants/url";

const CategoryModal = ({ open, onClose, categoryType, onSelect }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Reset states when modal closes
  useEffect(() => {
    if (!open) {
      setExpanded(null);
      setSelectedItem(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && categoryType) {
      fetchCategories();
    }
  }, [open, categoryType]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${URL}/api/category/fetch-categories?categoryType=${categoryType}`
      );
      setCategories(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Fixed: Make event parameter optional with default value
  const handleExpand = (name, event) => {
    // Check if event exists before calling stopPropagation
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    setExpanded(expanded === name ? null : name);
  };

  // Alternative approach: Separate handler for ListItemButton
  const handleSubCategoryClick = (sub) => {
    if (categoryType === "Pashu") {
      setExpanded(expanded === sub.name ? null : sub.name);
    } else {
      handleSelect(sub.name);
    }
  };

  const handleSelect = (subCategoryName, varietyName = "") => {
    setSelectedItem({ subCategoryName, varietyName });
    onSelect({
      subCategoryName,
      varietyName,
    });
    onClose();
  };

  const isItemSelected = (subCategoryName, varietyName = "") => {
    return (
      selectedItem?.subCategoryName === subCategoryName &&
      selectedItem?.varietyName === varietyName
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: colors.white,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          height: "80vh",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: 20,
          textAlign: "center",
          borderBottom: "1px solid #eee",
          pb: 1,
          color: colors.darkGreen,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          backgroundColor: colors.white,
          zIndex: 1,
        }}
      >
        {categoryType === "Pashu"
          ? "Select Animal Type & Variety"
          : categoryType === "Crops"
          ? "Select Crop Type"
          : "Select Tool Type"}
        <IconButton 
          onClick={onClose}
          size="small"
          sx={{ 
            color: colors.darkGreen,
            "&:hover": {
              backgroundColor: colors.lightMint,
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          height: "100%",
          maxHeight: "calc(80vh - 70px)",
          overflow: "auto", 
          bgcolor: "#fafafa",
          p: 0,
         
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: colors.green }} />
          </Box>
        ) : categories.length === 0 ? (
          <Typography textAlign="center" py={2} color="text.secondary">
            No categories found.
          </Typography>
        ) : (
          categories.map((cat) =>
            cat.subCategories.map((sub) => (
              <Box key={sub._id}>
                {/* ===== Subcategory Row ===== */}
                <ListItemButton
                  // Use the separate handler that doesn't expect event
                  onClick={() => handleSubCategoryClick(sub)}
                  sx={{
                    backgroundColor: isItemSelected(sub.name) 
                      ? colors.lightMint 
                      : colors.white,
                    borderRadius: 1,
                    mb: 0.5,
                    mx: 1,
                    mt: 0.5,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    border: isItemSelected(sub.name) 
                      ? `2px solid ${colors.green}`
                      : "1px solid transparent",
                    "&:hover": {
                      backgroundColor: colors.lightMint,
                      border: `1px solid ${colors.green}40`,
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemText
                    primary={sub.name}
                    primaryTypographyProps={{
                      fontWeight: isItemSelected(sub.name) ? 600 : 500,
                      fontSize: 15,
                      color: isItemSelected(sub.name) 
                        ? colors.darkGreen 
                        : "#000000",
                    }}
                  />
                  {categoryType === "Pashu" && (
                    <IconButton
                      size="small"
                      // This will pass the event correctly
                      onClick={(e) => handleExpand(sub.name, e)}
                      sx={{
                        color: colors.darkGreen,
                        "&:hover": {
                          backgroundColor: colors.green + "20",
                        }
                      }}
                    >
                      {expanded === sub.name ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )}
                </ListItemButton>

                {/* ===== Varieties (Only for Animals/Pashu) ===== */}
                {categoryType === "Pashu" && (
                  <Collapse
                    in={expanded === sub.name}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {/* Option to select just the subcategory without variety */}
                      <ListItemButton
                        sx={{
                          pl: 4,
                          py: 0.8,
                          backgroundColor: isItemSelected(sub.name, "") 
                            ? colors.lightMint 
                            : "#f9f9f9",
                          border: isItemSelected(sub.name, "") 
                            ? `2px solid ${colors.green}`
                            : "1px solid transparent",
                          "&:hover": {
                            backgroundColor: colors.lightMint,
                            border: `1px solid ${colors.green}40`,
                          },
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => handleSelect(sub.name, "")}
                      >
                        <ListItemText
                          primary={`All ${sub.name} Varieties`}
                          primaryTypographyProps={{
                            fontSize: 14,
                            color: isItemSelected(sub.name, "") 
                              ? colors.darkGreen 
                              : "#000000",
                            fontStyle: "italic",
                            fontWeight: isItemSelected(sub.name, "") ? 600 : 400,
                          }}
                        />
                      </ListItemButton>
                      
                      {/* Individual varieties */}
                      {sub.variety.map((v, i) => (
                        <ListItemButton
                          key={i}
                          sx={{
                            pl: 4,
                            py: 0.8,
                            backgroundColor: isItemSelected(sub.name, v) 
                              ? colors.lightMint 
                              : "#f9f9f9",
                            border: isItemSelected(sub.name, v) 
                              ? `2px solid ${colors.green}`
                              : "1px solid transparent",
                            "&:hover": {
                              backgroundColor: colors.lightMint,
                              border: `1px solid ${colors.green}40`,
                            },
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => handleSelect(sub.name, v)}
                        >
                          <ListItemText
                            primary={v}
                            primaryTypographyProps={{
                              fontSize: 14,
                              color: isItemSelected(sub.name, v) 
                                ? colors.darkGreen 
                                : colors.darkBlack,
                              fontWeight: isItemSelected(sub.name, v) ? 600 : 400,
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
                <Divider sx={{ my: 0.5 }} />
              </Box>
            ))
          )
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CategoryModal;